import { setUnionInto } from "collection-utils";

import { isArray, assert } from "../Support";
import { isLoadedDefined, RecomputingFromOneValueProducer } from "./component-helpers";
import type {
    ValueProducer,
    EnvironmentBoundEvaluable,
    PotentialValue,
    Loading,
    PrimitiveValue,
    ValueListener,
    Value,
    ForceValueProducer} from "./computation-types";
import {
    isPrimitiveValue,
    LOADING,
    isObjectValue
} from "./computation-types";
import { logError } from "../debug-print";
import { rowIndexColumnName } from "../google-sheets";
import type { BaseRowIndex} from "../firebase-function-types";
import { isBaseRowIndex } from "../firebase-function-types";

export function sortByRowIndex<T>(arr: Iterable<T>, getIndex: (x: T) => BaseRowIndex | undefined): T[] {
    return Array.from(arr).sort((a, b) => {
        const indexA = getIndex(a);
        const indexB = getIndex(b);
        if (indexA !== undefined) {
            if (indexB !== undefined) {
                if (indexA < indexB) return -1;
                if (indexA === indexB) return 0;
                return 1;
            } else {
                return -1;
            }
        } else {
            if (indexB !== undefined) {
                return 1;
            } else {
                return 0;
            }
        }
    });
}

function getPrimitivesForNeedle<O, A>(
    needle: Value<O, A>,
    force: ForceValueProducer<O, A>,
    isArrayType: boolean
): PrimitiveValue[] {
    if (needle === undefined) {
        if (isArrayType) {
            return [];
        } else {
            return [undefined];
        }
    }

    const values: PrimitiveValue[] = [];
    if (isArray(needle)) {
        for (const vp of needle) {
            const v = force(vp);
            if (!isLoadedDefined(v) || !isPrimitiveValue(v)) continue;
            values.push(v);
        }
    } else if (isPrimitiveValue(needle)) {
        values.push(needle);
    } else {
        logError("Needle is neither an array nor a primitive", needle);
    }

    return values;
}

interface TableIndexListener {
    indexUpdated(): void;
}

// FIXME: Keys and key arrays could be shared across rows, which makes it hard
// to remove all data associated with a row.  Should we use weak maps for all those?
// But if we do, can we be sure that we're not losing connections from rows to their
// old primitive values, for example?
export class TableIndex<O, A> implements ValueListener<O, A> {
    private readonly _subscriptionCounts = new Map<ValueProducer<O, A>, number>();

    private _keysForRows = new Map<ValueProducer<O, A>, ValueProducer<O, A>>();
    // We will use this in the future. See https://github.com/quicktype/glide/issues/4460
    // private readonly _valuesForKeys = new Map<ValueProducer<O, A>, PrimitiveValue | readonly ValueProducer<O, A>[]>();
    private readonly _rowsForPrimitives = new Map<PrimitiveValue, Set<ValueProducer<O, A>>>();

    private readonly _listeners: Set<TableIndexListener> = new Set();

    private _isDirty: boolean = true;
    private _recomputing: boolean = false;

    constructor(
        private readonly _rows: ValueProducer<O, A>,
        private readonly _keyForRow: EnvironmentBoundEvaluable<O, A>,
        private readonly _isArrayType: boolean
    ) {}

    public subscribe(listener: TableIndexListener): void {
        assert(!this._listeners.has(listener));
        this._listeners.add(listener);
    }

    public unsubscribe(listener: TableIndexListener): void {
        assert(this._listeners.has(listener));
        this._listeners.delete(listener);
    }

    private subscribeTo(vp: ValueProducer<O, A>): void {
        let count = this._subscriptionCounts.get(vp);
        if (count === undefined) {
            count = 0;
            vp.subscribe(this);
        }
        this._subscriptionCounts.set(vp, count + 1);
    }

    private unsubscribeFrom(vp: ValueProducer<O, A>, howOften: number = 1): void {
        let count = this._subscriptionCounts.get(vp);
        assert(count !== undefined);
        count -= howOften;
        assert(count >= 0);
        if (count > 0) {
            this._subscriptionCounts.set(vp, count);
        } else {
            vp.unsubscribe(this);
            this._subscriptionCounts.delete(vp);
        }
    }

    public setDirty(_sender: ValueProducer<O, A>): void {
        // Right now we recomputing everything when one of our
        // dependences is dirtied.  In the future we'll want to
        // recompute more selectively.  Concretely, `sender` can be:
        //
        // 1. The rows array: Rows might have been added or removed.
        //    For removed rows: Remove the row from all data structures.
        //    For added rows: Mark the new row dirty.
        // 2. The key for a row: For all old primitive values for
        //    that row: clear the row set for the value and mark all
        //    those rows dirty.
        // 3. An array element of a key for a row: clear the row set
        //    for the old primitive value and mark all those rows
        //    dirty.

        if (this._isDirty) return;
        this._isDirty = true;

        for (const listener of this._listeners) {
            listener.indexUpdated();
        }
    }

    private recomputeIfNecessary(): void {
        if (!this._isDirty) return;

        if (this._recomputing) {
            logError("Recursive index recomputing detected - aborting");
            return;
        }
        this._recomputing = true;

        const oldCounts = Array.from(this._subscriptionCounts);

        const existingKeysForRows = this._keysForRows;
        this._keysForRows = new Map();

        // this._valuesForKeys.clear();
        this._rowsForPrimitives.clear();

        this.build(existingKeysForRows);

        for (const [vp, c] of oldCounts) {
            this.unsubscribeFrom(vp, c);
        }

        this._recomputing = false;
        this._isDirty = false;
    }

    private subscribeToPrimitivesForKey(keyVP: ValueProducer<O, A>): PrimitiveValue[] | Loading | undefined {
        this.subscribeTo(keyVP);
        const value = keyVP.current;
        if (value === LOADING) return LOADING;

        const values: PrimitiveValue[] = [];
        if (isArray(value)) {
            const vps: ValueProducer<O, A>[] = [];
            for (const vp of value) {
                this.subscribeTo(vp);
                vps.push(vp);
                const v = vp.current;
                if (!isLoadedDefined(v) || !isPrimitiveValue(v)) continue;
                // this._valuesForKeys.set(vp, v);
                values.push(v);
            }
            // this._valuesForKeys.set(keyVP, vps);
        } else if (value === undefined) {
            if (!this._isArrayType) {
                values.push(undefined);
            }
        } else if (isPrimitiveValue(value)) {
            // this._valuesForKeys.set(keyVP, value);
            values.push(value);
        } else {
            logError("Needle is neither an array nor a primitive", value);
        }

        if (values.length === 0) return undefined;

        return values;
    }

    private build(existingKeysForRows: Map<ValueProducer<O, A>, ValueProducer<O, A>>): void {
        this.subscribeTo(this._rows);
        const rows = this._rows.current;
        // If rows are loading, everything is loading.  See `getRowsForNeedle`.
        if (!isArray(rows)) return;

        for (const rowVP of rows) {
            let keyVP = existingKeysForRows.get(rowVP);
            if (keyVP === undefined) {
                keyVP = this._keyForRow.eval(rowVP);
            }
            this._keysForRows.set(rowVP, keyVP);

            const primitives = this.subscribeToPrimitivesForKey(keyVP);
            // FIXME: Technically, if even one of the row's primitives
            // are loading, everything is loading.
            if (!isLoadedDefined(primitives)) continue;

            for (const p of primitives) {
                let rowsForPrimitive = this._rowsForPrimitives.get(p);
                if (rowsForPrimitive === undefined) {
                    rowsForPrimitive = new Set();
                    this._rowsForPrimitives.set(p, rowsForPrimitive);
                }
                rowsForPrimitive.add(rowVP);
            }
        }
    }

    public getRowsForNeedle(needle: PrimitiveValue): ReadonlySet<ValueProducer<O, A>> | Loading | undefined {
        this.recomputeIfNecessary();

        const rows = this._rows.current;
        if (!isLoadedDefined(rows)) return rows;

        return this._rowsForPrimitives.get(needle);
    }
}

export class FilterWhereIdenticalValueProducer<O, A>
    extends RecomputingFromOneValueProducer<O, A>
    implements TableIndexListener {
    private _tableIndex: TableIndex<O, A> | undefined;

    constructor(
        appEnvironment: A,
        needle: ValueProducer<O, A>,
        private readonly _isArrayType: boolean,
        private readonly _getTableIndex: () => TableIndex<O, A>
    ) {
        super(appEnvironment, needle);
    }

    private getTableIndex(): TableIndex<O, A> {
        if (this._tableIndex === undefined) {
            this._tableIndex = this._getTableIndex();
        }
        return this._tableIndex;
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        // ##compiledRecomputeShortcut
        // This is one of the cases (the only one?) of a
        // `RecomputingValueProducer`s that does its own subscribing in
        // addition to whatever `force` does.
        this.getTableIndex().subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this.getTableIndex().unsubscribe(this);
    }

    public indexUpdated(): void {
        this.setDirty();
    }

    protected recomputeFromInput(needle: Value<O, A>, force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const tableIndex = this.getTableIndex();
        const primitives = getPrimitivesForNeedle(needle, force, this._isArrayType);

        const allRows = new Set<ValueProducer<O, A>>();
        for (const p of primitives) {
            const rows = tableIndex.getRowsForNeedle(p);
            if (rows === undefined) continue;
            if (rows === LOADING) return LOADING;

            setUnionInto(allRows, rows);
        }

        return sortByRowIndex(allRows, r => {
            const data = r.current;
            if (!isObjectValue(data)) return undefined;
            const index = data[rowIndexColumnName]?.current;
            if (!isBaseRowIndex(index)) return undefined;
            return index;
        });
    }
}
